import React, { useState, useMemo, useCallback } from 'react';
import Table from '../../components/Table/Table';
import BillForm from '../../components/Form/BillForm';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';
import { Bill, TableColumn } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import {
  useBills,
  useCreateBill,
  useUpdateBill,
  useDeleteBill,
} from '../../hooks/useBills';
import { useProviders } from '../../hooks/useProviders';
import { useCurrencies } from '../../hooks/useCurrencies';

const Bills: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingBill, setEditingBill] = useState<Bill | undefined>();

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 },
  }), []);

  const { data: bills, isLoading, error, refetch } = useBills(queryOptions);
  const { data: providers } = useProviders(queryOptions);
  const { data: currencies = [] } = useCurrencies();

  const createMutation = useCreateBill(() => {
    setShowForm(false);
    setEditingBill(undefined);
    setTimeout(() => refetch(), 100);
  });

  const updateMutation = useUpdateBill(() => {
    setShowForm(false);
    setEditingBill(undefined);
    setTimeout(() => refetch(), 100);
  });

  const deleteMutation = useDeleteBill(() => {
    setTimeout(() => refetch(), 100);
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [currencyFilter, setCurrencyFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [amountFromFilter, setAmountFromFilter] = useState<string>('');
  const [amountToFilter, setAmountToFilter] = useState<string>('');

  // providerId holds the provider documentId; older records may hold the numeric id
  const getProviderName = useCallback((providerId: any) => {
    if (!providerId || !providers) return '-';
    if (typeof providerId === 'object') return providerId.name || '-';
    const provider = providers.find(
      (p: any) => p.documentId === providerId || String(p.id) === String(providerId)
    );
    return provider ? provider.name : '-';
  }, [providers]);

  const filteredBills = useMemo(() => {
    if (!bills) return [];
    return bills.filter(bill => {
      const providerName = getProviderName(bill.providerId);
      const matchesSearch = !searchTerm || (
        providerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bill.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bill.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bill.status.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesProvider = providerFilter === 'all' || String(bill.providerId) === String(providerFilter);
      const matchesCurrency = currencyFilter === 'all' || resolveCurrencyCode(bill.currency, currencies) === currencyFilter;
      const dueDate = new Date(bill.dueDate);
      const matchesDateFrom = !dateFromFilter || dueDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || dueDate <= new Date(dateToFilter);
      const matchesAmountFrom = !amountFromFilter || bill.amount >= parseFloat(amountFromFilter);
      const matchesAmountTo = !amountToFilter || bill.amount <= parseFloat(amountToFilter);
      return matchesSearch && matchesProvider && matchesCurrency &&
        matchesDateFrom && matchesDateTo && matchesAmountFrom && matchesAmountTo;
    });
  }, [bills, currencies, searchTerm, providerFilter, currencyFilter, dateFromFilter, dateToFilter, amountFromFilter, amountToFilter, getProviderName]);

  const buildExportRow = useCallback((bill: Bill) => ({
    'Provider': getProviderName(bill.providerId),
    'Category': bill.category,
    'Description': bill.description || '',
    'Original Amount': bill.originalAmount ?? bill.amount,
    'Remaining Amount': bill.amount,
    'Currency': resolveCurrencyCode(bill.currency, currencies),
    'Status': bill.status,
    'Due Date': new Date(bill.dueDate).toLocaleDateString()
  }), [getProviderName, currencies]);

  const handleExport = () => {
    exportToExcel(filteredBills.map(buildExportRow), `bills_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    printAsExcel(filteredBills.map(buildExportRow), 'Bills');
  };

  const filters: FilterConfig[] = [
    { key: 'provider', label: 'Provider', type: 'select', value: providerFilter, onChange: setProviderFilter, options: [{ value: 'all', label: 'All Providers' }, ...(providers || []).map((p: any) => ({ value: p.documentId || p.id, label: p.name }))] },
    { key: 'currency', label: 'Currency', type: 'select', value: currencyFilter, onChange: setCurrencyFilter, options: [
      { value: 'all', label: 'All Currencies' },
      ...(currencies as any[])
        .filter((c: any) => c.isActive !== false && c.code)
        .map((c: any) => ({ value: c.code, label: c.code }))
    ] },
    { key: 'dateFrom', label: 'Due Date From', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Due Date To', type: 'date', value: dateToFilter, onChange: setDateToFilter },
    { key: 'amountFrom', label: 'Min Amount', type: 'number', value: amountFromFilter, onChange: setAmountFromFilter, placeholder: '0.00', step: '0.01' },
    { key: 'amountTo', label: 'Max Amount', type: 'number', value: amountToFilter, onChange: setAmountToFilter, placeholder: '999999.99', step: '0.01' },
  ];

  const handleClearFilters = () => {
    setSearchTerm('');
    setProviderFilter('all');
    setCurrencyFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
    setAmountFromFilter('');
    setAmountToFilter('');
  };

  const columns: TableColumn[] = [
    { key: 'providerId', label: 'Provider', sortable: true, render: (value: string) => getProviderName(value) },
    { key: 'originalAmount', label: 'Original Amount', sortable: true, render: (value: number | undefined, row: any) => {
      const currency = resolveCurrencyCode(row.currency, currencies);
      const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency;
      // Older bills predate the originalAmount field; fall back to amount
      return `${symbol} ${Number(value ?? row.amount ?? 0).toLocaleString()}`;
    } },
    { key: 'amount', label: 'Remaining Amount', sortable: true, render: (value: number, row: any) => {
      const currency = resolveCurrencyCode(row.currency, currencies);
      const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency;
      return `${symbol} ${value.toLocaleString()}`;
    } },
    { key: 'currency', label: 'Currency', sortable: true, render: (value: any) => resolveCurrencyCode(value, currencies) },
    { key: 'status', label: 'Status', sortable: true, render: (value: string) => {
      let color = 'bg-gray-100 text-gray-800';
      if (value === 'paid') color = 'bg-green-100 text-green-800';
      else if (value === 'overdue') color = 'bg-red-100 text-red-800';
      else if (value === 'pending') color = 'bg-yellow-100 text-yellow-800';
      return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>{value}</span>;
    } },
    { key: 'dueDate', label: 'Due Date', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
    { key: 'category', label: 'Category', sortable: true },
    { key: 'description', label: 'Description', sortable: false },
    { key: 'createdAt', label: 'Created', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
  ];

  const handleEdit = (bill: Bill) => {
    setEditingBill(bill);
    setShowForm(true);
  };

  const handleDelete = async (bill: Bill) => {
    if (!window.confirm(`Are you sure you want to delete bill ${bill.id}?`)) return;
    if (!bill?.id) return;
    try {
      await deleteMutation.execute(bill);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleAdd = () => {
    setEditingBill(undefined);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: Partial<Bill>) => {
    try {
      const curr = data.currency as any;
      const currencyValue = typeof curr === 'object' ? (curr?.documentId || curr?.id) : curr;
      if (editingBill?.id) {
        await updateMutation.execute({ id: editingBill.id, data: { ...data, currency: currencyValue } });
      } else {
        await createMutation.execute({
          providerId: data.providerId,
          amount: data.amount || 0,
          originalAmount: data.originalAmount ?? data.amount ?? 0,
          currency: currencyValue,
          exchangeRate: data.exchangeRate,
          dueDate: data.dueDate,
          status: data.status || 'pending',
          category: data.category,
          description: data.description,
          deductionAmount: data.deductionAmount,
          deductionDescription: data.deductionDescription,
        });
      }
    } catch (error) {
      console.error('Form submission failed:', error);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingBill(undefined);
  };

  if (isLoading) return <TableSkeleton />;
  if (error) return <ErrorState message="Error loading bills. Please try again." onRetry={refetch} entityName="bills" />;

  if (showForm) {
    return (
      <div className="space-y-6">
        <BillForm bill={editingBill} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Bills" description="Manage your bills to providers and payment status" />
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search bills..."
        filters={filters}
        onClearFilters={handleClearFilters}
        totalCount={bills?.length ?? 0}
        filteredCount={filteredBills.length}
        entityName="bills"
        onAdd={handleAdd}
        addLabel="Add Bill"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <Table
        columns={columns}
        data={filteredBills}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
        title=""
        searchPlaceholder=""
      />
    </div>
  );
};

export default Bills;
